import React, { useState, useEffect } from 'react';
import { add, format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, setHours, setMinutes, setSeconds } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Event, Resource, Template } from './scheduling/types';
import DayColumn from './scheduling/DayColumn';
import EventModal from './scheduling/EventModal';
import TemplateModal from './scheduling/TemplateModal';
import CalendarHeader from './scheduling/CalendarHeader';
import CalendarControls from './scheduling/CalendarControls';
import CalendarView from './scheduling/CalendarView';
import TemplatesDisplay from './scheduling/TemplatesDisplay';
import { supabase } from '../../lib/supabaseClient';

// Add type declaration for the window object to include our custom property
declare global {
  interface Window {
    eventBeingEdited?: boolean;
  }
}

const SchedulingSystem: React.FC = () => {
  // State variables
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [view, setView] = useState<'day' | 'week'>('week');
  const [isEventModalOpen, setIsEventModalOpen] = useState<boolean>(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState<boolean>(false);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [modalDefaultDate, setModalDefaultDate] = useState<Date | null>(null);
  const [currentTemplate, setCurrentTemplate] = useState<Template | null>(null);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [staffMembers, setStaffMembers] = useState<Array<{id: string, name: string, role: string, photo: string}>>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [fetchVersion, setFetchVersion] = useState(0);
  
  // Load staff members from team table
  useEffect(() => {
    // Add event listener for editing events
    const handleEditEvent = (e: any) => {
      const event = e.detail;
      setCurrentEvent(event);
      setModalDefaultDate(null);
      setIsEventModalOpen(true);
    };

    window.addEventListener('edit-event', handleEditEvent);
    
    const fetchStaff = async () => {
      const { data, error } = await supabase
        .from('team')
        .select('id, name, role, photo')
        .eq('is_active', true)
        .order('display_order');
        
      if (data && !error) {
        setStaffMembers(data);
        // Find 'Larina' and set as default selected staff
        const larina = data.find(staff => staff.name === 'Larina');
        if (larina) {
          setSelectedStaffId(larina.id);
        } else if (data.length > 0) {
          // Fallback to the first staff member if Larina is not found
          setSelectedStaffId(data[0].id);
        }
      } else if (error) {
        // console.error('Error fetching staff:', error);
      }
    };
    
    fetchStaff();
    
    // Cleanup event listener when component unmounts
    return () => {
      window.removeEventListener('edit-event', handleEditEvent);
    };
  }, []);

  // Create Resource objects from staffMembers
  useEffect(() => {
    if (staffMembers.length > 0) {
      const updatedResources: Resource[] = staffMembers.map((staff, index) => ({
        id: index + 1, 
        name: staff.name,
        staffId: staff.id 
      }));
      setResources(updatedResources);
    }
  }, [staffMembers]);

  // Fetch appointments and blocked times
  useEffect(() => {
    const fetchAppointmentsAndBlockedTimes = async () => {
      if (resources.length === 0 && staffMembers.length > 0) {
        return;
      }
      if (staffMembers.length === 0) {
        return;
      }
      
      // Only fetch data when the view or date changes, not on every state update
      const weekStart = startOfWeek(currentDate, { locale: enUS });
      const weekEnd = endOfWeek(currentDate, { locale: enUS });
      let allDisplayEvents: Event[] = [];

      try {
        // Fetch both data types in parallel for better performance
        const [appointmentsResponse, availabilityResponse] = await Promise.all([
          supabase
            .from('appointments')
            .select('*, user_profiles(full_name, preferred_name)')
            .gte('start_time', weekStart.toISOString())
            .lte('end_time', weekEnd.toISOString()),
          supabase
            .from('staff_availability')
            .select('*')
            .gte('specific_date', format(weekStart, 'yyyy-MM-dd'))
            .lte('specific_date', format(weekEnd, 'yyyy-MM-dd'))
        ]);

        // Process appointments
        if (!appointmentsResponse.error && appointmentsResponse.data) {
          const activeAppointments = appointmentsResponse.data.filter(apt => apt.status !== 'cancelled');
          const processedAppointments = activeAppointments.map(apt => {
            const linkedResource = resources.find(r => r.staffId === apt.staff_id);
            if (!linkedResource) return null;
            // @ts-ignore
            const clientProfile = apt.user_profiles;
            const clientName = clientProfile ? (clientProfile.preferred_name || clientProfile.full_name || 'Unknown Client') : (apt.client_id ? `Client ID: ${apt.client_id.substring(0,8)}` : 'N/A');
            return {
              id: apt.id, 
              title: apt.service_id ? `Appt for Service: ${apt.service_id.substring(0,8)}` : 'Appointment',
              start: new Date(apt.start_time + 'Z').toISOString(),
              end: new Date(apt.end_time + 'Z').toISOString(),
              resourceId: linkedResource.id,
              client: clientName,
              status: apt.status as Event['status'],
              notes: apt.notes || '',
              recurring: false, 
              isBlockedTime: false,
            };
          }).filter(event => event !== null) as Event[]; 
          allDisplayEvents = allDisplayEvents.concat(processedAppointments);
        }

        // Process blocked times
        if (!availabilityResponse.error && availabilityResponse.data) {
          const fetchedBlockedTimes = availabilityResponse.data.map(block => {
            const linkedResource = resources.find(r => r.staffId === block.staff_id);
            if (!linkedResource) return null;
            return {
              id: block.id,
              title: block.notes || 'Blocked Time',
              start: `${block.specific_date}T${block.start_time}Z`,
              end: `${block.specific_date}T${block.end_time}Z`,
              resourceId: linkedResource.id, 
              client: '',
              status: 'cancelled' as Event['status'], 
              notes: block.notes || '',
              recurring: block.is_recurring || false,
              isBlockedTime: true,
            };
          }).filter(event => event !== null) as Event[];
          allDisplayEvents = allDisplayEvents.concat(fetchedBlockedTimes);
        }
      } catch (err) {
        console.error('Error fetching calendar data:', err);
      }
      
      setEvents(allDisplayEvents);
    };

    fetchAppointmentsAndBlockedTimes();
  }, [currentDate, resources, view, fetchVersion]);

  // Computed values
  const weekDays = eachDayOfInterval({
    start: startOfWeek(currentDate, { locale: enUS }),
    end: endOfWeek(currentDate, { locale: enUS })
  });
  
  // Debug weekdays information
  // console.log('Week days:', weekDays.map(d => format(d, 'yyyy-MM-dd')));
  
  // Helper functions
  const formatDateRange = (): string => {
    const start = startOfWeek(currentDate);
    const end = endOfWeek(currentDate);
    
    if (view === 'day') {
      return format(currentDate, 'MMMM d, yyyy');
    }
    
    // Week view
    if (format(start, 'MMM') === format(end, 'MMM')) {
      return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
    } else {
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
  };
  
  const navigateToPrev = (): void => {
    const newDate = view === 'day' ? add(currentDate, { days: -1 }) : add(currentDate, { weeks: -1 });
    setCurrentDate(newDate);
  };
  
  const navigateToNext = (): void => {
    const newDate = view === 'day' ? add(currentDate, { days: 1 }) : add(currentDate, { weeks: 1 });
    setCurrentDate(newDate);
  };
  
  const navigateToToday = (): void => {
    setCurrentDate(new Date());
  };
  
  const handleOpenEventModal = (dateForModal: Date | null = null): void => {
    if (!window.eventBeingEdited) {
      setCurrentEvent(null);
      setModalDefaultDate(dateForModal);
    } else {
      setModalDefaultDate(null);
    }
    window.eventBeingEdited = false;
    setIsEventModalOpen(true);
  };
  
  // Delete event handler with optimistic UI update
  const handleDeleteEvent = async (eventId: string | number): Promise<void> => {
    try {
      // Optimistically remove from UI
      const eventToDelete = events.find(e => e.id === eventId);
      if (!eventToDelete) return;
      
      // Update local state immediately
      setEvents(prev => prev.filter(e => e.id !== eventId));
      
      // Then actually delete from database
      if (eventToDelete.isBlockedTime) {
        await supabase
          .from('staff_availability')
          .delete()
          .eq('id', eventId);
      } else {
        // Update appointment status to cancelled instead of deleting
        await supabase
          .from('appointments')
          .update({ status: 'cancelled' })
          .eq('id', eventId);
      }
      
    } catch (err) {
      console.error('Error deleting event:', err);
      // If error, refresh events to get accurate state
      setFetchVersion(prev => prev + 1);
    }
  };
  
  const handleOpenTemplateModal = (template: Template | null = null): void => {
    setCurrentTemplate(template);
    setIsTemplateModalOpen(true);
  };
  
  const handleSaveTemplate = (templateData: Template): void => {
    if (templateData.id) {
      setTemplates(prevTemplates => prevTemplates.map(template => template.id === templateData.id ? {...templateData} : template));
    } else {
      setTemplates(prevTemplates => [...prevTemplates, {...templateData, id: (prevTemplates.length > 0 ? Math.max(...prevTemplates.map(t => typeof t.id === 'number' ? t.id : 0)) : 0) + 1}]);
    }
    setIsTemplateModalOpen(false);
  };
  
  const hasConflicts = (event: Event | null): boolean => {
    if (!event || event.isBlockedTime) return false; // Don't check conflicts for blocked time itself
    return events.some(e => 
      e.id !== event.id && 
      !e.isBlockedTime && // Only check against other non-blocked events
      e.resourceId === event.resourceId &&
      new Date(e.start) < new Date(event.end) &&
      new Date(e.end) > new Date(event.start)
    );
  };

  // Filter events based on selected staff member
  const filteredEvents = selectedStaffId
    ? events.filter(event => {
        const resourceForEvent = resources.find(r => r.id === event.resourceId);
        if (!resourceForEvent) return false; 
        return resourceForEvent.staffId === selectedStaffId;
      })
    : events; // This fallback might be hit if selectedStaffId is null initially before Wen Li is set

  // Debug info
  // console.log('Current date:', currentDate);
  // console.log('Staff members:', staffMembers);
  // console.log('Resources:', resources);
  // console.log('Selected staff ID:', selectedStaffId);
  // console.log('Current events:', events);
  // console.log('Filtered events:', filteredEvents);

  return (
    <div className="space-y-6 pt-2">
      <CalendarHeader 
        onAddEventClick={() => handleOpenEventModal()} 
        onTemplatesClick={() => handleOpenTemplateModal()}
      />
      
      {/* Calendar Controls Container (bg-white rounded-lg shadow p-4) */}
      <div className="bg-white rounded-lg shadow p-4">
        <CalendarControls 
          currentDate={currentDate}
          view={view}
          formatDateRange={formatDateRange}
          onNavigatePrev={navigateToPrev}
          onNavigateToday={navigateToToday}
          onNavigateNext={navigateToNext}
          onSetView={setView}
        />
        
        {/* Staff Selector */}
        <div className="mb-4">
          <label htmlFor="staff-selector" className="block text-sm font-medium text-gray-700 mb-1">
            Select Staff
          </label>
          <select
            id="staff-selector"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#3A5E63] focus:border-[#3A5E63]"
            value={selectedStaffId || ''}
            onChange={(e) => setSelectedStaffId(e.target.value || null)}
          >
            {staffMembers.map(staff => (
              <option key={staff.id} value={staff.id}>
                {staff.name} ({staff.role})
              </option>
            ))}
          </select>
        </div>
        
        <CalendarView 
          view={view}
          weekDays={weekDays} 
          currentDate={currentDate}
          events={filteredEvents}
          onOpenEventModal={handleOpenEventModal}
          onDeleteEvent={handleDeleteEvent}
        />
      </div>
      
      <TemplatesDisplay 
        templates={templates}
        onOpenTemplateModal={handleOpenTemplateModal}
        onApplyTemplate={(templateId) => alert(`Template ${templateId} applied!`)} // Placeholder action
      />
      
      {/* Event Modal */}
      <EventModal 
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          // Only trigger a re-fetch if an event was actually created or modified
          if (currentEvent) {
            setFetchVersion(prevVersion => prevVersion + 1);
          }
          setCurrentEvent(null);
          setModalDefaultDate(null);
        }}
        currentEvent={currentEvent}
        resources={resources} 
        modalDefaultDate={modalDefaultDate}
        onEventCreated={(newEvent) => {
          // Add the new event to the local state to avoid a full re-fetch
          setEvents(prev => [...prev, newEvent]);
        }}
        onEventUpdated={(updatedEvent) => {
          // Update the existing event in the local state
          setEvents(prev => prev.map(event => 
            event.id === updatedEvent.id ? updatedEvent : event
          ));
        }}
      />
      
      {/* Template Modal */}
      <TemplateModal 
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onSave={handleSaveTemplate}
        currentTemplate={currentTemplate}
        resources={resources}
      />
    </div>
  );
};

export default SchedulingSystem;
